import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';

import { Typography } from '../Typography';
import type { TypographyVariant } from '../Typography';

import styles from './Checkbox.module.css';

export type CheckboxSize = 'sm' | 'md';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type' | 'onChange'
> {
  size?: CheckboxSize;
  /** Renders the mixed (dash) state. Sets the underlying input's `indeterminate` property. */
  indeterminate?: boolean;
  /** Fires with the next checked value. */
  onChange?: (checked: boolean) => void;
  /** Primary label beside the box. */
  label?: ReactNode;
  /** Secondary line under the label. */
  supportingText?: ReactNode;
}

const LABEL_VARIANT: Record<CheckboxSize, TypographyVariant> = { sm: 'text-sm', md: 'text-md' };

// Checkmark geometry exported from the Figma "check" node (14×14).
function CheckGlyph() {
  return (
    <svg className={styles.glyph} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M11.6667 3.5L5.25001 9.91667L2.33334 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DashGlyph() {
  return (
    <svg className={styles.glyph} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M2.91669 7H11.0834"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Checkbox with checked / unchecked / indeterminate states across two sizes. State is
 * driven by native pseudo-classes, so it works controlled or uncontrolled; keyboard focus
 * shows the focus ring (WCAG 2.4.11).
 *
 * @interim Ported from the reference apps; replaceable by the UI Kit without API breakage.
 */
export function Checkbox({
  size = 'md',
  indeterminate = false,
  onChange,
  label,
  supportingText,
  disabled,
  className,
  ...rest
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(event.target.checked);
  };

  return (
    <label
      className={classNames(
        styles.wrapper,
        styles[`size-${size}`],
        disabled && styles.disabled,
        className,
      )}
    >
      <span className={styles.control}>
        <input
          ref={inputRef}
          type="checkbox"
          className={styles.input}
          disabled={disabled}
          onChange={handleChange}
          {...rest}
        />
        <span className={styles.box} aria-hidden="true">
          <span className={styles.check}>
            <CheckGlyph />
          </span>
          <span className={styles.dash}>
            <DashGlyph />
          </span>
        </span>
      </span>
      {label != null ? (
        <span className={styles.text}>
          <Typography
            as="span"
            variant={LABEL_VARIANT[size]}
            weight="medium"
            className={classNames(styles.label)}
          >
            {label}
          </Typography>
          {supportingText != null ? (
            <Typography
              as="span"
              variant={LABEL_VARIANT[size]}
              className={classNames(styles.supporting)}
            >
              {supportingText}
            </Typography>
          ) : null}
        </span>
      ) : null}
    </label>
  );
}
